import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils/cn";
import formatCurrency from "@/utils/formatCurrency";
import { formatPriceOrNA } from "@/utils/formatPriceOrNA";
import { Plus } from "@pkg/icons";

const PlanListCardUIName = ({ children }: { children: React.ReactNode }) => (
  <h3
    data-component="PlanListCardUIName"
    className="font-archivo text-base leading-[140%] font-normal tracking-[0.16px] text-[#474747] sm:text-xl sm:tracking-[-0.2px]"
  >
    {children}
  </h3>
);

const PlanListCardUIDescription = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <div
    data-component="PlanListCardUIDescription"
    className="font-archivo text-sm leading-[170%] font-light tracking-[0.14px] text-[#323232]"
  >
    {children}
  </div>
);

const PlanListCardUIFeatureList = ({ features }: { features: string[] }) => (
  <ul
    data-component="PlanListCardUIFeatureList"
    className="list-disc space-y-1 pl-4"
  >
    {features.map((feature, index) => (
      <li
        key={index}
        className="font-archivo text-sm leading-[170%] font-light tracking-[0.14px] text-[#323232]"
      >
        {feature}
      </li>
    ))}
  </ul>
);

const PlanListCardUIOriginalPrice = ({
  price,
  currency,
}: {
  price: number;
  currency: string;
}) => {
  if (!price || !Number.isFinite(price)) return null;

  return (
    <span
      data-component="PlanListCardUIOriginalPrice"
      className="font-archivo text-sm leading-[140%] font-light tracking-[-0.18px] text-error-primary line-through"
    >
      {formatCurrency(price, currency, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}
    </span>
  );
};

const PlanListCardUIPricing = ({
  price,
  strikePrice,
  billingLabel,
  currency,
}: {
  price: number | undefined;
  strikePrice?: number;
  billingLabel: string;
  currency: string;
}) => {
  const showStrikePrice =
    price !== undefined &&
    strikePrice != null &&
    Number.isFinite(strikePrice) &&
    strikePrice > price;

  return (
    <div
      data-component="PlanListCardUIPricing"
      className="flex items-end gap-1"
    >
      {showStrikePrice && (
        <PlanListCardUIOriginalPrice price={strikePrice} currency={currency} />
      )}
      <div className="flex items-end gap-0.5">
        <span className="font-archivo text-xl leading-[140%] font-normal tracking-[-0.2px] text-[#474747]">
          {formatPriceOrNA(price, currency, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          })}
        </span>
        {price !== undefined && (
          <span className="ml-1 font-archivo text-base leading-[140%] font-light tracking-[0.16px] text-[#323232]">
            /{billingLabel}
          </span>
        )}
      </div>
    </div>
  );
};

const PlanListCardUIQuantitySelector = ({
  quantity,
  onQuantityChange,
}: {
  quantity: number;
  onQuantityChange: (quantity: number) => void;
}) => (
  <div data-component="PlanListCardUIQuantitySelector" className="w-16">
    <Select
      value={quantity.toString()}
      onValueChange={(value) => onQuantityChange(parseInt(value))}
    >
      <SelectTrigger
        className="h-9 w-full border-[#323232] px-3 py-1 focus:ring-0"
        onClick={(e) => e.stopPropagation()}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="1">1</SelectItem>
        <SelectItem value="2">2</SelectItem>
        <SelectItem value="3">3</SelectItem>
      </SelectContent>
    </Select>
  </div>
);

const PlanListCardUIAddButton = ({
  onClick,
  disabled,
}: {
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
}) => (
  <Button
    data-component="PlanListCardUIAddButton"
    variant="outline"
    size="icon-sm"
    onClick={onClick}
    disabled={disabled}
  >
    <Plus className="" />
  </Button>
);

// --- Main PlanListCardUI component ---

type PlanListCardUIProps = {
  productRefId: string;
  name: string;
  description: string | null;
  featureList: string[];
  price: number | undefined;
  strikePrice?: number;
  onAdd: (
    productRefId: string,
    quantity: number,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => void;
  billingLabel: string;
  currency?: string;
  //
  maxCount?: number;
  quantity?: number;
  onQuantityChange?: (quantity: number) => void;
  //
  addDisabled?: boolean;
  className?: string;
};

const PlanListCardUI = ({
  productRefId,
  name,
  description,
  featureList,
  price,
  strikePrice,
  onAdd,
  billingLabel,
  currency = "USD",
  maxCount,
  quantity,
  onQuantityChange,
  addDisabled,
  className = "",
}: PlanListCardUIProps) => {
  const showQuantitySelector =
    quantity != null && maxCount && maxCount > 1 && onQuantityChange != null;

  return (
    <div
      data-component="PlanListCardUI"
      data-plan={productRefId}
      aria-label={`Add ${name}`}
      className={cn(
        "flex w-full flex-wrap items-center justify-between gap-4 rounded-xl border border-edge-light bg-white p-4 text-left",
        addDisabled && "opacity-50",
        className,
      )}
    >
      <div className="flex flex-col gap-1">
        <PlanListCardUIName>{name}</PlanListCardUIName>
        {description && (
          <PlanListCardUIDescription>{description}</PlanListCardUIDescription>
        )}
        {featureList.length > 0 && (
          <PlanListCardUIFeatureList features={featureList} />
        )}
      </div>

      <div className="ml-auto flex items-center gap-4">
        <PlanListCardUIPricing
          price={price}
          strikePrice={strikePrice}
          billingLabel={billingLabel}
          currency={currency}
        />

        {showQuantitySelector && (
          <PlanListCardUIQuantitySelector
            quantity={quantity}
            onQuantityChange={onQuantityChange}
          />
        )}
        <PlanListCardUIAddButton
          disabled={addDisabled}
          onClick={(e) => {
            e.stopPropagation();
            onAdd(productRefId, quantity ?? 1, e);
          }}
        />
      </div>
    </div>
  );
};

export default PlanListCardUI;
